import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Post {
  id: number;
  title: string;
  content: string;
  category: string;
  author_id: string | null;
  views: number;
  created_at: string;
}

interface Notice {
  id: number;
  title: string;
  content: string;
  is_important: boolean;
  views: number;
  created_at: string;
}

interface SportsArticle {
  id: number;
  title: string;
  sport_type: string;
  source: string;
  views: number;
  likes: number;
  comments: number;
  created_at: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function useHomeData() {
  const [notices, setNotices] = useState<{ id: number; title: string; date: string }[]>([]);
  const [noticesLoading, setNoticesLoading] = useState(true);

  const [hotIssues, setHotIssues] = useState<{ id: number; category: string; categoryColor: string; title: string; time: string; views: number }[]>([]);
  const [hotIssuesLoading, setHotIssuesLoading] = useState(true);

  const [communityPosts, setCommunityPosts] = useState<{ id: number; rank: number; title: string; likes: number; isHot: boolean }[]>([]);
  const [communityPostsLoading, setCommunityPostsLoading] = useState(true);

  const [scamCases, setScamCases] = useState<{ id: number; title: string; views: number; date: string }[]>([]);

  const [sportsArticles, setSportsArticles] = useState<SportsArticle[]>([]);
  const [sportsArticlesLoading, setSportsArticlesLoading] = useState(true);

  // Fetch notices — independent
  useEffect(() => {
    let cancelled = false;

    async function fetchNotices() {
      try {
        const { data, error } = await supabase
          .from("notices")
          .select("id, title, created_at")
          .order("created_at", { ascending: false })
          .limit(5);

        if (!cancelled && data && !error) {
          setNotices(
            data.map((n: Notice) => ({
              id: n.id,
              title: n.title,
              date: formatDate(n.created_at),
            }))
          );
        }
      } finally {
        if (!cancelled) setNoticesLoading(false);
      }
    }

    fetchNotices();
    return () => { cancelled = true; };
  }, []);

  // Fetch hot issues — independent
  useEffect(() => {
    let cancelled = false;

    async function fetchHotIssues() {
      try {
        const { data, error } = await supabase
          .from("posts")
          .select("id, title, category, views, created_at")
          .in("category", ["scam_report", "issue", "guide"])
          .order("created_at", { ascending: false })
          .limit(8);

        if (!cancelled && data && !error) {
          const categoryColorMap: Record<string, string> = {
            scam_report: "bg-red-500/20 text-red-400",
            issue: "bg-accent-500/20 text-accent-400",
            guide: "bg-secondary-400/20 text-secondary-400",
          };
          const categoryLabelMap: Record<string, string> = {
            scam_report: "먹튀제보",
            issue: "이슈",
            guide: "이용가이드",
          };
          setHotIssues(
            data.map((p: Post) => ({
              id: p.id,
              category: categoryLabelMap[p.category] || p.category,
              categoryColor: categoryColorMap[p.category] || "bg-accent-500/20 text-accent-400",
              title: p.title,
              time: formatTime(p.created_at),
              views: p.views,
            }))
          );
        }
      } finally {
        if (!cancelled) setHotIssuesLoading(false);
      }
    }

    fetchHotIssues();
    return () => { cancelled = true; };
  }, []);

  // Fetch community posts — independent
  useEffect(() => {
    let cancelled = false;

    async function fetchCommunityPosts() {
      try {
        const { data: communityData, error } = await supabase
          .from("posts")
          .select("id, title, created_at")
          .in("category", ["community", "promote", "tip"])
          .order("created_at", { ascending: false })
          .limit(20);

        if (!cancelled && communityData && !error && communityData.length > 0) {
          const postIds = communityData.map((p: Post) => p.id);

          const { data: likeData } = await supabase
            .from("post_likes")
            .select("post_id")
            .in("post_id", postIds);

          const likeMap: Record<number, number> = {};
          if (likeData) {
            likeData.forEach((l) => {
              likeMap[l.post_id] = (likeMap[l.post_id] || 0) + 1;
            });
          }

          const sorted = communityData
            .map((p: Post) => ({ ...p, likeCount: likeMap[p.id] || 0 }))
            .sort((a, b) => b.likeCount - a.likeCount)
            .slice(0, 5);

          setCommunityPosts(
            sorted.map((p, idx) => ({
              id: p.id,
              rank: idx + 1,
              title: p.title,
              likes: p.likeCount,
              isHot: idx < 3,
            }))
          );
        }
      } finally {
        if (!cancelled) setCommunityPostsLoading(false);
      }
    }

    fetchCommunityPosts();
    return () => { cancelled = true; };
  }, []);

  // Fetch scam reports — lightweight, fire-and-forget
  useEffect(() => {
    let cancelled = false;

    async function fetchScamCases() {
      try {
        const { data, error } = await supabase
          .from("posts")
          .select("id, title, views, created_at")
          .eq("category", "scam_report")
          .order("created_at", { ascending: false })
          .limit(3);

        if (!cancelled && data && !error) {
          setScamCases(
            data.map((p: Post) => ({
              id: p.id,
              title: p.title,
              views: p.views,
              date: formatDate(p.created_at),
            }))
          );
        }
      } catch { /* silent */ }
    }

    fetchScamCases();
    return () => { cancelled = true; };
  }, []);

  // Fetch sports articles — independent
  useEffect(() => {
    let cancelled = false;

    async function fetchSportsArticles() {
      try {
        const sportTypes = ["soccer", "baseball", "basketball", "volleyball", "hockey"];
        const allSportsArticles: SportsArticle[] = [];
        const seenIds = new Set<number>();

        for (const st of sportTypes) {
          const { data, error } = await supabase
            .from("sports_articles")
            .select("id, title, sport_type, source, views, created_at")
            .eq("sport_type", st)
            .order("created_at", { ascending: false })
            .limit(10);

          if (!cancelled && data && !error) {
            for (const a of (data as SportsArticle[])) {
              if (!seenIds.has(a.id)) {
                seenIds.add(a.id);
                allSportsArticles.push(a);
              }
            }
          }
        }

        if (!cancelled && allSportsArticles.length > 0) {
          const sArticleIds = allSportsArticles.map((a: SportsArticle) => a.id);

          const { data: sLikeData } = await supabase
            .from("article_likes")
            .select("article_id")
            .in("article_id", sArticleIds);

          const sLikeMap: Record<number, number> = {};
          if (sLikeData) {
            sLikeData.forEach((l) => {
              sLikeMap[l.article_id] = (sLikeMap[l.article_id] || 0) + 1;
            });
          }

          const { data: sCommentData } = await supabase
            .from("article_comments")
            .select("article_id")
            .in("article_id", sArticleIds);

          const sCommentMap: Record<number, number> = {};
          if (sCommentData) {
            sCommentData.forEach((c) => {
              sCommentMap[c.article_id] = (sCommentMap[c.article_id] || 0) + 1;
            });
          }

          setSportsArticles(
            allSportsArticles.map((a: SportsArticle) => ({
              id: a.id,
              title: a.title,
              sport_type: a.sport_type,
              source: a.source,
              views: a.views || 0,
              likes: sLikeMap[a.id] || 0,
              comments: sCommentMap[a.id] || 0,
              created_at: a.created_at,
            }))
          );
        }
      } finally {
        if (!cancelled) setSportsArticlesLoading(false);
      }
    }

    fetchSportsArticles();
    return () => { cancelled = true; };
  }, []);

  return {
    notices,
    noticesLoading,
    hotIssues,
    hotIssuesLoading,
    communityPosts,
    communityPostsLoading,
    scamCases,
    safetyTips: [],
    rankings: [],
    sportsArticles,
    sportsArticlesLoading,
  };
}